/* Pure URL helpers for the Times Contrarian embed.
   No React, no DOM mutation — everything here is a plain function so the
   mapping between app paths and paper paths can be reasoned about (and tested)
   on its own. */

import { TC_KNOWN_ORIGINS } from "./tc-embed-config";

// Hosts that count as "this app" when we see an absolute url. window.location
// .hostname covers whatever we are actually served from (nya-dev, a preview
// host, localhost) without needing to enumerate it.
const NYA_HOSTS = new Set(
  [
    "neilyoungarchives.com",
    "www.neilyoungarchives.com",
    "nya-dev.shakeypictures.com",
    "localhost",
    "127.0.0.1",
    typeof window !== "undefined" && window.location
      ? window.location.hostname
      : "",
  ].filter(Boolean)
);

// The app paths that existed before the paper moved out to WordPress. They are
// already shared in the wild, so they have to keep working — but there is no
// article behind them any more, so they open the paper's front page rather
// than being framed literally (which would request TC_ORIGIN + "/1/").
const LEGACY_NEWS_PATH = /^\/(\d+|top-40-tracks|top-10-albums)(\/article)?\/?$/;

const stripLeadingHash = (url) => {
  const prefix = ["/#/", "#/", "#"].find((mix) => url.indexOf(mix) === 0);
  return prefix ? url.replace(prefix, "/") : url;
};

/* Turn an absolute or hash-style NYA url into a path react-router can take.
   Extracted from the anchor handler in excerpt.js so there is a single
   definition of this conversion. */
export function normalizeNyaPath(raw) {
  let url = stripLeadingHash(String(raw || ""));
  if (url.startsWith("http")) {
    try {
      const parsed = new URL(url);
      url = parsed.pathname + parsed.search + parsed.hash;
    } catch (error) {
      console.warn("[tc-url] could not parse url", raw, error);
      return "/";
    }
    // An old-style link (https://host/#/album?id=1) parses to pathname "/" with
    // the real route sitting in the hash, so it needs stripping a second time.
    url = stripLeadingHash(url);
  }
  return url.startsWith("/") ? url : `/${url}`;
}

/* Decide what an inbound tc-nav url means.

   The paper sends the same message type for two different events: the reader
   navigated within the paper, and the reader clicked an NYA link inside it.
   The url's origin is the only thing that separates them.

     tc       -> in-paper navigation: sync our address bar, send the share url
     nya      -> click-through: route the app and let the iframe unmount
     external -> ignore; the paper's own bridge still owns outbound links */
export function classifyNavUrl(raw) {
  const value = String(raw || "");
  if (!value) return { kind: "external", path: "" };

  const isRelative = !/^[a-z][a-z0-9+.-]*:/i.test(value) && !value.startsWith("//");

  let parsed;
  try {
    parsed = new URL(value, window.location.origin);
  } catch (error) {
    console.warn("[tc-url] could not parse tc-nav url", raw, error);
    return { kind: "external", path: "" };
  }

  if (TC_KNOWN_ORIGINS.indexOf(parsed.origin) !== -1) {
    return { kind: "tc", path: parsed.pathname + parsed.search };
  }

  if (isRelative || NYA_HOSTS.has(parsed.hostname)) {
    return { kind: "nya", path: normalizeNyaPath(value) };
  }

  return { kind: "external", path: value };
}

/* /news/some-article  ->  /some-article/     (framed by the iframe)
   /news, /news/1, /news/3/article?id=x  ->  /   (the paper's front page) */
export function appPathToTcPath(pathname = "/") {
  const remainder = String(pathname).replace(/^\/news/, "");
  if (!remainder || remainder === "/") return "/";

  const [path] = remainder.split("?");
  if (LEGACY_NEWS_PATH.test(path)) return "/";

  // WordPress canonicalises to a trailing slash; sending it avoids a redirect
  // on every deep link. Paths that look like a file are left alone.
  const lastSegment = path.split("/").filter(Boolean).pop() || "";
  if (path.endsWith("/") || lastSegment.indexOf(".") !== -1) return remainder;
  return remainder.replace(path, `${path}/`);
}

/* /some-article/  ->  /news/some-article/ */
export function tcPathToAppPath(tcPath = "/") {
  const path = String(tcPath);
  if (!path || path === "/") return "/news";
  return `/news${path.startsWith("/") ? path : `/${path}`}`.replace(/\/{2,}/g, "/");
}
